import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Fix invalid locale "en-US@posix" used by lightweight-charts in some environments
const _origToLocaleString = Date.prototype.toLocaleString;
Date.prototype.toLocaleString = function(locale, ...args) {
  try {
    return _origToLocaleString.call(this, locale, ...args);
  } catch {
    const safeLocale = typeof locale === 'string' ? locale.replace(/@.*$/, '') : 'en-US';
    return _origToLocaleString.call(this, safeLocale, ...args);
  }
};

// Suppress benign global errors that Chrome fires (extensions, ResizeObserver, disposed charts)
const SUPPRESSED_PATTERNS = [
  'disposed', 'isDisposed', 'Invalid language tag',
  'ResizeObserver loop',
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  // Chrome extension errors
  'Extension context invalidated',
  'Script error.',
];

function shouldSuppress(msg) {
  if (!msg || typeof msg !== 'string') return false;
  const lower = msg.toLowerCase();
  return SUPPRESSED_PATTERNS.some(p => lower.includes(p.toLowerCase()));
}

const origError = window.onerror;
window.onerror = function(msg) {
  if (shouldSuppress(msg)) return true;
  if (origError) return origError.apply(this, arguments);
};

window.addEventListener('error', (e) => {
  if (shouldSuppress(e.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
}, true);

window.addEventListener('unhandledrejection', (e) => {
  const msg = e.reason?.message || '';
  if (shouldSuppress(msg)) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
}, true);

// ---- Auto-recover devices stuck on an outdated cached version ----
// Some devices (PWA / old service worker) keep serving an old build that can point to a
// stale server URL ("serveur indisponible") or use outdated logic (e.g. old PIN flow).
// When the deployed version differs from what this device last loaded, purge all caches +
// service workers and reload once (guarded by localStorage, so it never loops).
(async () => {
  try {
    const res = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' });
    if (!res.ok) return;
    const data = await res.json();
    const current = data && data.version;
    if (!current) return;
    const stored = localStorage.getItem('app_build_version');
    if (stored && stored !== current) {
      localStorage.setItem('app_build_version', current);
      try {
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map(r => r.unregister()));
        }
        if (window.caches) {
          const keys = await caches.keys();
          await Promise.all(keys.map(k => caches.delete(k)));
        }
      } catch (e) { /* ignore cache purge errors */ }
      window.location.reload();
      return;
    }
    if (!stored) localStorage.setItem('app_build_version', current);
  } catch (e) { /* offline or no version file: ignore */ }
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register PWA service worker with update handling
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then(registration => {
        // Check for updates periodically
        setInterval(() => registration.update(), 60 * 60 * 1000);
      })
      .catch(() => {});
  });
}

// Global error reporter for uncaught errors (uses the function from ErrorBoundary)
window.addEventListener('error', (e) => {
  if (!shouldSuppress(e.message) && typeof window.__reportErrorToBackend === 'function') {
    window.__reportErrorToBackend(e.error || { message: e.message, stack: '' }, null, 'runtime');
  }
}, false);

window.addEventListener('unhandledrejection', (e) => {
  const msg = e.reason?.message || '';
  if (!shouldSuppress(msg) && typeof window.__reportErrorToBackend === 'function') {
    window.__reportErrorToBackend(e.reason || { message: msg }, null, 'unhandled');
  }
}, false);
